import { axiosApiInstance } from "@/lib/axios";
import { API_PORTAL_URL } from "@/lib/config";
import { ITravelGuide } from "@/types/travelGuide.types";

export const mockTravelGuidesData: Record<string, ITravelGuide> = {
  "ahmedabad-to-rann": {
    title: "Ahmedabad to Rann of Kutch",
    subtitle: "Travel Options, Cost & Duration",
    distance: "400 km",
    image:
      "https://images.unsplash.com/photo-1670923331633-be262e035a9a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1200",
    overview:
      "Ahmedabad, the largest city in Gujarat, serves as the perfect starting point for your journey to the magnificent Rann of Kutch. With multiple travel options available, you can choose the one that best suits your budget and schedule.",
    roadTravel: {
      duration: "7-8 hours",
      distance: "400 km",
      cost: "₹6,000 - ₹10,000",
      description:
        "The most popular and scenic way to reach Rann of Kutch is by road. The journey takes you through the heart of Gujarat, offering glimpses of rural life and beautiful landscapes.",
      options: [
        {
          type: "Private Car/Taxi",
          cost: "₹8,000 - ₹10,000",
          duration: "7-8 hours",
          pros: [
            "Flexible timing",
            "Comfortable journey",
            "Stop at scenic points",
            "Door-to-door service",
          ],
          recommended: true,
        },
        {
          type: "Bus (Gujarat State Transport)",
          cost: "₹500 - ₹800",
          duration: "9-10 hours",
          pros: ["Budget-friendly", "Multiple departures", "Safe and reliable"],
          recommended: false,
        },
        {
          type: "Self-Drive",
          cost: "₹6,000 - ₹8,000 (rental + fuel)",
          duration: "7-8 hours",
          pros: [
            "Complete freedom",
            "Explore at your pace",
            "Adventure experience",
          ],
          recommended: false,
        },
      ],
      route: [
        "Start from Ahmedabad",
        "NH47 to Viramgam (60 km)",
        "Continue on NH947 to Samakhiali (180 km)",
        "Take SH42 to Bhuj (80 km)",
        "From Bhuj to Dhordo (80 km)",
        "Total Distance: 400 km",
      ],
    },
    flightTravel: {
      duration: "1 hour flight + 2 hours drive",
      cost: "₹3,500 - ₹8,000",
      description:
        "Flying to Bhuj is the fastest way to reach close to Rann of Kutch. Regular flights operate between Ahmedabad and Bhuj.",
      details: [
        {
          title: "Airlines",
          items: ["Air India", "IndiGo", "SpiceJet"],
        },
        {
          title: "Flight Duration",
          items: ["1 hour (direct)"],
        },
        {
          title: "Frequency",
          items: ["Daily flights available"],
        },
        {
          title: "From Bhuj Airport",
          items: [
            "Hire taxi to Dhordo (80 km, 1.5-2 hours)",
            "Cost: ₹2,000 - ₹3,000",
          ],
        },
      ],
      totalCost: "Flight: ₹3,500 - ₹8,000 + Taxi: ₹2,000 - ₹3,000",
      totalDuration: "3-4 hours (including ground travel)",
    },
    trainTravel: {
      duration: "9-10 hours + 2 hours drive",
      cost: "₹500 - ₹2,000",
      description:
        "Train travel is a comfortable and economical option. Multiple trains run from Ahmedabad to Bhuj.",
      trains: [
        {
          name: "Kutch Express",
          number: "19115",
          departure: "11:50 PM",
          arrival: "08:55 AM",
          duration: "9 hours 5 min",
          days: "Daily",
        },
        {
          name: "Bhuj Passenger",
          number: "59562",
          departure: "05:15 AM",
          arrival: "05:00 PM",
          duration: "11 hours 45 min",
          days: "Daily",
        },
        {
          name: "Gandhidham SF Express",
          number: "22957",
          departure: "11:35 PM",
          arrival: "07:25 AM",
          duration: "7 hours 50 min",
          days: "Daily",
        },
      ],
      classes: [
        { type: "3AC", cost: "₹800 - ₹1,200" },
        { type: "2AC", cost: "₹1,200 - ₹1,800" },
        { type: "1AC", cost: "₹2,000 - ₹3,000" },
      ],
      fromStation:
        "Taxi from Bhuj Railway Station to Dhordo: ₹2,000 - ₹3,000 (80 km, 1.5-2 hours)",
    },
  },
  "vadodara-to-rann": {
    title: "Vadodara to Rann of Kutch",
    subtitle: "Travel Options, Cost & Duration",
    distance: "480 km",
    image:
      "https://images.unsplash.com/photo-1706013729724-caada9be0f97?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1200",
    overview:
      "From the cultural capital of Gujarat, Vadodara, you can easily reach the mesmerizing Rann of Kutch through various modes of transport.",
  },
  "surat-to-rann": {
    title: "Surat to Rann of Kutch",
    subtitle: "Travel Options, Cost & Duration",
    distance: "450 km",
    image:
      "https://images.unsplash.com/photo-1706013698821-3f417f9fcc0b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1200",
    overview:
      "Traveling from Surat, the diamond city of India, to the white desert is an exciting journey that can be done via road, train, or flight.",
  },
  "bhuj-to-rann": {
    title: "Bhuj to Rann of Kutch",
    subtitle: "Travel Options, Cost & Duration",
    distance: "80 km",
    image:
      "https://images.unsplash.com/photo-1709917524188-3f442929bacd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1200",
    overview:
      "Bhuj is the closest major city to Rann of Kutch, making it the most convenient base for exploring the white desert and Rann Utsav.",
  },
};

/**
 * Service to fetch a Travel Guide by its URL slug.
 * 1. Primary: POST to /TravelGuide/FindByURLTravelGuide with body { URL: slug }
 * 2. Secondary/Fallback: Look up locally in mockTravelGuidesData
 */
export async function fetchGuideBySlug(
  slug: string,
): Promise<ITravelGuide | null> {
  const cleanSlug = slug.trim().toLowerCase();

  try {
    const response = await axiosApiInstance.post(
      `${API_PORTAL_URL}/Attraction/AttractionFindByURL`,
      { AttractionURL: cleanSlug },
    );

    if (response.data && response.data.Data) {
      return response.data.Data;
    }
  } catch (error) {
    console.warn(
      "Primary API POST for fetchGuideBySlug failed, invoking local fallback:",
      error,
    );
  }

  // Fallback to local mock data
  const localMatched = mockTravelGuidesData[cleanSlug];
  if (localMatched) {
    return localMatched;
  }

  return null;
}
